import { Dialog, DialogContent, DialogTitle, Divider, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Form, Formik } from 'formik';
import { formatISO } from 'date-fns';
import { RaptorJobInfoStep } from '../steps/RaptorJobInfoStep';
import { RaptorJobValidationSchema, RaptorJobFormValues } from '../../../RaptorJobValidationSchema';
import { RaptorJobResourcesStep } from '../steps/RaptorJobResourcesStep';
import { CreateModalDialogActions } from './CreateModalDialogActions';
import { ResourceUnavailabilityWarningModal } from '@/common/presentation/components/ResourceUnavailabilityWarningModal';
import { useRaptorJobCreateModal } from '../hook/useRaptorJobCreateModal';
import { useRaptorJobMutations } from '../hook/useRaptorJobMutations';
import tid from '@/common/infrastructure/config/tid';
import { JOB_FORM_STEPS, JOB_MODAL_TITLES, JOB_MODAL_SUBTITLES } from '@/raptorjob/domain/constants/jobFormLabels';

interface RaptorJobCreateFormProps {
  handleClose: () => void;
  openCreatedJob: (id: string) => void;
}

export const RaptorJobCreateForm = ({ handleClose, openCreatedJob }: RaptorJobCreateFormProps) => {
  const {
    step,
    setStep,
    hasUnavailableResources,
    setHasUnavailableResources,
    showResourceUnavailabilityWarningModal,
    setShowResourceUnavailabilityWarningModal,
    jobSiteSearchTerm,
    setJobSiteSearchTerm,
    selectedCustomerName,
    setSelectedCustomerName,
    crewFields,
    setCrewFields,
    selectedEquipment,
    setSelectedEquipment,
    selectedClass,
    setSelectedClass,
    selectedType,
    setSelectedType,
    selectedSize,
    setSelectedSize,
    equipmentSearchTerm,
    setEquipmentSearchTerm
  } = useRaptorJobCreateModal();

  const { handleSubmit } = useRaptorJobMutations({
    handleClose,
    openCreatedJob,
    selectedEquipment
  });

  return (
    <>
      <Dialog
        open
        onClose={handleClose}
        fullWidth
        slotProps={{
          paper: {
            sx: {
              maxWidth: {
                xs: '343px',
                sm: '568px',
                md: '700px'
              }
            }
          }
        }}
      >
        <Formik<RaptorJobFormValues>
          initialValues={{
            customerId: null,
            jobSiteId: null,
            jobTypeId: null,
            jobDescription: '',
            recurrence: 'single',
            startDate: null,
            estimatedDurationDays: null,
            endDate: null,
            startTime: null,
            endTime: null,
            showUpTime: null,
            leaveByTime: null,
            onJobTime: null,
            calledAt: formatISO(new Date()),
            notesForCrew: '',
            jobNotes: '',
            estimates: [],
            groups: [],
            extraSystemIds: []
          }}
          validationSchema={RaptorJobValidationSchema()}
          onSubmit={handleSubmit}
          validateOnChange
          validateOnBlur
        >
          {(helpers) => {
            return (
              <>
                <Form noValidate>
                  <DialogTitle>
                    {JOB_MODAL_TITLES.NEW_JOB}
                    <Typography variant="caption" component="p">
                      {step === JOB_FORM_STEPS.JOB_INFO && JOB_MODAL_SUBTITLES.COMPLETE_DETAILS}
                      {step === JOB_FORM_STEPS.RESOURCES && `${JOB_MODAL_SUBTITLES.ADD_RESOURCES} - ${selectedCustomerName}`}
                    </Typography>
                  </DialogTitle>
                  <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={(theme) => ({
                      position: 'absolute',
                      right: 8,
                      top: 8,
                      color: theme.palette.grey[500]
                    })}
                    {...tid('jobs:create:modal:close-button')}
                  >
                    <CloseIcon />
                  </IconButton>
                  <Divider />
                  <DialogContent sx={{ padding: { xs: 2, sm: '20px 24px' } }}>
                    {step === JOB_FORM_STEPS.JOB_INFO && (
                      <RaptorJobInfoStep
                        jobSiteSearchTerm={jobSiteSearchTerm}
                        setJobSiteSearchTerm={setJobSiteSearchTerm}
                        setSelectedCustomerName={setSelectedCustomerName}
                      />
                    )}
                    {step === JOB_FORM_STEPS.RESOURCES && (
                      <RaptorJobResourcesStep
                        onUnavailableResources={setHasUnavailableResources}
                        recurrence={helpers.values.recurrence}
                        crewFields={crewFields}
                        setCrewFields={setCrewFields}
                        selectedEquipment={selectedEquipment}
                        setSelectedEquipment={setSelectedEquipment}
                        selectedClass={selectedClass}
                        setSelectedClass={setSelectedClass}
                        selectedType={selectedType}
                        setSelectedType={setSelectedType}
                        selectedSize={selectedSize}
                        setSelectedSize={setSelectedSize}
                        equipmentSearchTerm={equipmentSearchTerm}
                        setEquipmentSearchTerm={setEquipmentSearchTerm}
                      />
                    )}
                  </DialogContent>
                  <CreateModalDialogActions
                    onSubmit={async () => {
                      if (hasUnavailableResources) {
                        setShowResourceUnavailabilityWarningModal(true);
                        return;
                      }

                      await handleSubmit(helpers.values, helpers);
                    }}
                    step={step}
                    setStep={setStep}
                    handleClose={handleClose}
                  />
                </Form>

                {showResourceUnavailabilityWarningModal && (
                  <ResourceUnavailabilityWarningModal
                    open={showResourceUnavailabilityWarningModal}
                    onClose={() => setShowResourceUnavailabilityWarningModal(false)}
                    onConfirm={async () => {
                      handleSubmit(helpers.values, helpers);
                      setShowResourceUnavailabilityWarningModal(false);
                      setHasUnavailableResources(false);
                    }}
                    confirmText="Create Job & Job Ticket"
                    cancelText="Cancel"
                  />
                )}
              </>
            );
          }}
        </Formik>
      </Dialog>
    </>
  );
};
